/**
 * Renders assistant message body (parts, references, streaming caret).
 */

import { createTextWithReferences } from './text-with-references.js';
import { renderBlock } from './blocks/block-dispatcher.js';
import { createReferencesPanel } from './references-panel.js';
import { createStatusStep } from './status-step.js';
import { shouldShowStreamingCaret } from './message-content-utils.js';
import { t } from '../../../i18n.js';

/**
 * Whether an entity_choice block in this message is resolved by a later user message.
 * @param {Array} allMessages
 * @param {string} messageId
 * @returns {boolean}
 */
export function isChoiceBlockResolved(allMessages, messageId) {
  const idx = allMessages.findIndex((m) => m._id === messageId);
  if (idx === -1) return false;
  return allMessages.slice(idx + 1).some((m) => m.role === 'user');
}

/**
 * @param {Object} options
 * @param {Object} options.message
 * @param {boolean} [options.isStreaming]
 * @param {boolean} [options.showThinking]
 * @param {Array} [options.allMessages]
 * @param {function(string): void} [options.onChoiceSelect]
 * @param {Object} [options.listSelection]
 * @returns {HTMLElement}
 */
export function createAssistantMessageBody({
  message,
  isStreaming = false,
  showThinking = false,
  allMessages = [],
  onChoiceSelect,
  listSelection,
}) {
  const body = document.createElement('div');
  body.className = 'ai-assistant-message-body';

  if (showThinking && isStreaming) {
    const thinking = document.createElement('div');
    thinking.className = 'ai-thinking';
    thinking.setAttribute('aria-hidden', 'true');
    thinking.textContent = t('ai_thinking');
    body.appendChild(thinking);
  }

  const parts = message.parts;
  const references = message.references || [];
  const hasParts = parts && parts.length > 0;
  const choiceResolved = isChoiceBlockResolved(allMessages, message._id);

  if (hasParts) {
    parts.forEach((part, partIndex) => {
      if (part.kind === 'text') {
        body.appendChild(createTextWithReferences(part.content, references));
      } else if (part.kind === 'block') {
        const el = renderBlock(part.block, {
          choiceResolved,
          onChoiceSelect: isStreaming ? undefined : onChoiceSelect,
          messageId: message._id,
          partIndex,
          listSelection,
        });
        if (el) body.appendChild(el);
      } else if (part.kind === 'status') {
        body.appendChild(createStatusStep(part.status));
      } else if (part.content) {
        const fallback = document.createElement('span');
        fallback.textContent = String(part.content);
        body.appendChild(fallback);
      }
    });
  } else if (message.content) {
    body.appendChild(createTextWithReferences(message.content, references));
  }

  if (isStreaming && !showThinking && shouldShowStreamingCaret(message)) {
    const caret = document.createElement('span');
    caret.className = 'ai-streaming-caret';
    caret.setAttribute('aria-hidden', 'true');
    body.appendChild(caret);
  }

  if (references.length > 0 && !isStreaming) {
    const panel = createReferencesPanel({
      references,
      parts: hasParts ? parts : undefined,
      fallbackContent: hasParts ? undefined : message.content,
    });
    if (panel) body.appendChild(panel);
  }

  return body;
}
